/**
 * MOTEUR DE L'IMPORT SCODOC — appariement, contrôle de forme, lignes à transmettre
 * ==============================================================================
 * Spec : doc/modules/etudiants.md §0.5 (ce que SCODOC apporte) · §0.7 (le format
 *        ScoDoc) · §0.8 (comment les colonnes sont appariées) · §Onglet 2 section B
 * Lot 1.4. Module PUR : aucune dépendance à React, aucune requête réseau.
 *
 * ── CE QUI LE SÉPARE DU MOTEUR APOGEE, ET POURQUOI IL EST À PART ─────────────
 * `apogeeImport.js` porte une liste blanche FIGÉE de dix-huit colonnes, avec leur
 * intitulé mesuré dans le fichier réel. Ici, la liste est PARAMÉTRABLE : l'onglet
 * Paramétrage porte `scodoc_colonnes_mapping`, une carte « colonne du fichier » →
 * « champ de la base », que l'utilisateur remplit lui-même.
 *
 * C'est une différence de nature, pas de degré : les colonnes ne sont plus connues à
 * l'écriture du code. Fusionner les deux moteurs aurait demandé de rendre le premier
 * paramétrable pour n'en profiter jamais — la spec dit explicitement le contraire
 * pour APOGEE : « dix-huit réglages seraient dix-huit occasions de se tromper ».
 *
 * ── CE QU'IL PARTAGE, ET NE RÉÉCRIT PAS ──────────────────────────────────────
 * La normalisation des intitulés (l'espace final, le double espace, les majuscules),
 * le découpage d'une feuille (la plage déclarée jusqu'à 753 pour 62 lignes de
 * données), le parseur de collage à état, le seuil de conformité et son plancher.
 * Tout cela vient de `apogeeImport.js`, et vient de là parce qu'il n'y a aucune
 * raison que les deux imports se trompent différemment sur les mêmes pièges.
 *
 * ── LA LISTE BLANCHE RESTE CELLE DU SERVEUR ──────────────────────────────────
 * Les champs autorisés ne sont pas décidés ici : ils arrivent de
 * `GET /parametrage` (`champsScodoc`, miroir de `CHAMPS_SCODOC_AUTORISES`). Ce
 * module n'apporte que la FORME de chacun. Un champ autorisé dont la forme n'est pas
 * déclarée ici n'est pas deviné : il est signalé, et l'analyse le dit à l'écran —
 * même discipline que côté serveur, où le module refuse carrément de se charger.
 */

import {
    normaliserIntitule,
    decouperFeuille,
    matriceDepuisTexte,
    convertirEntier,
    FORME_NUMERO_ETUDIANT,
    SEUIL_CONFORMITE,
    PLANCHER_NON_CONFORMES
} from './apogeeImport';

/** Cellule ramenée à une chaîne utile. Même convention que le moteur APOGEE. */
function texte(brut) {
    if (brut === null || brut === undefined) return '';
    if (brut instanceof Date) return brut.toISOString().slice(0, 10);
    return String(brut).replace(/\s+/g, ' ').trim();
}

const estVide = (brut) => texte(brut) === '';

// ═══════════════════════════════════════════════════════════════════════════════
// 1. LA FORME DE CHAQUE CIBLE POSSIBLE
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Ce que ce moteur sait lire, champ de destination par champ de destination.
 *
 * ── DEUX RANGS, DEUX PLAFONDS, ET LE PLAFOND N'EST PAS UN DÉTAIL ─────────────
 * Mesuré sur les fichiers réels le 2026-08-27 :
 *
 *   `classement_recrutement`  1 → 9999. Il classe TOUS LES CANDIDATS (mesuré
 *                             11 → 210, colonne `classement` de l'export de
 *                             recrutement) ; un IUT à forte demande dépasserait le
 *                             millier sans que rien soit anormal.
 *   `rang_promotion`          1 → 999. Il classe UNE PROMOTION (mesuré 1 → 41,
 *                             colonne `Rg` d'un relevé de notes). Une promotion de
 *                             plus de mille étudiants n'existe pas dans un
 *                             département d'IUT.
 *
 * Le plafond est ce qui refuse un rang d'admission collé par erreur dans la colonne
 * du rang de promotion : un fichier mal branché se trahit alors sur la première
 * valeur au-dessus de 999, au lieu d'écrire un nombre plausible et faux.
 *
 * Les phrases « un entier de 1 à … » sont reprises MOT POUR MOT du serveur
 * (`FORMES_SCODOC`) : deux vocabulaires pour la même règle feraient croire à deux
 * règles.
 */
export const CIBLES_SCODOC = {
    classement_recrutement: {
        libelle: "Rang à l'admission",
        forme: 'un entier de 1 à 9999',
        valider: (b) => convertirEntier(b, 1, 9999) !== null,
        convertir: (b) => convertirEntier(b, 1, 9999)
    },
    rang_promotion: {
        libelle: 'Rang dans la promotion',
        forme: 'un entier de 1 à 999',
        // ⚠️ Ce champ EXIGE le semestre du relevé, demandé sur la carte d'import : un
        // rang de promotion change à chaque semestre, et sans lui « 12 / 41 » est un
        // nombre que personne ne peut dater. Le serveur refuse l'écriture sans, et la
        // base aussi (contrainte `ei_rang_promotion_date`, v39).
        exigeSemestre: true,
        valider: (b) => convertirEntier(b, 1, 999) !== null,
        convertir: (b) => convertirEntier(b, 1, 999)
    }
};

/** La clé de liaison : un numéro étudiant, comme pour APOGEE (§0.4). */
const CHAMP_CLE = {
    libelle: 'Numéro étudiant',
    forme: 'un numéro étudiant à 8 chiffres',
    valider: (b) => FORME_NUMERO_ETUDIANT.test(texte(b)),
    convertir: (b) => texte(b)
};

// ═══════════════════════════════════════════════════════════════════════════════
// 2. APPARIEMENT DES COLONNES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Apparie les en-têtes du fichier à la clé de liaison et aux colonnes du mapping.
 *
 * @param {Array}  entetes    première ligne du fichier, telle que lue
 * @param {Object} options
 * @param {string} options.cleLiaison        `reglages.scodoc_cle_liaison` (défaut `code_nip`)
 * @param {Object} options.mapping           `{ "colonne du fichier": "champ de la base" }`
 * @param {Object} options.champsAutorises   `champsScodoc` renvoyé par le serveur
 */
export function apparierColonnesScodoc(entetes, { cleLiaison, mapping, champsAutorises }) {
    const normalisees = (entetes || []).map(normaliserIntitule);
    const utilisees = new Set();

    const chercher = (intitule) => {
        const attendu = normaliserIntitule(intitule);
        if (!attendu) return null;
        const i = normalisees.findIndex((h, k) => h === attendu && !utilisees.has(k));
        if (i >= 0) utilisees.add(i);
        return i >= 0 ? i : null;
    };

    const iCle = chercher(cleLiaison);
    const colonnes = [{
        cle: 'code_etudiant',
        role: 'cle',
        intituleAttendu: cleLiaison,
        intituleSource: iCle !== null ? String(entetes[iCle] ?? '') : null,
        index: iCle,
        cible: 'code_etudiant',
        libelle: CHAMP_CLE.libelle,
        forme: CHAMP_CLE.forme,
        trouvee: iCle !== null
    }];

    // Les cibles nommées par le mapping mais que ce moteur ne sait pas lire. Elles ne
    // sont pas devinées : elles sont dites. Le cas se produirait le jour où une
    // migration ouvrirait un champ à SCODOC sans que ce fichier suive.
    const ciblesInconnues = [];

    for (const [intitule, cible] of Object.entries(mapping || {})) {
        // Le serveur refuse déjà d'enregistrer un mapping vers un champ non autorisé ;
        // on le revérifie ici pour que l'écran ne propose jamais d'écrire dans le vide
        // si le réglage a été posé avant que la liste blanche ne change.
        if (champsAutorises && !(cible in champsAutorises)) {
            ciblesInconnues.push({ intitule, cible, motif: 'champ non autorisé par le paramétrage' });
            continue;
        }
        const forme = CIBLES_SCODOC[cible];
        if (!forme) {
            ciblesInconnues.push({ intitule, cible, motif: 'forme non déclarée dans le moteur' });
            continue;
        }
        const index = chercher(intitule);
        colonnes.push({
            cle: cible,
            role: 'donnee',
            intituleAttendu: intitule,
            intituleSource: index !== null ? String(entetes[index] ?? '') : null,
            index,
            cible,
            libelle: forme.libelle,
            forme: forme.forme,
            trouvee: index !== null
        });
    }

    // Le §0.7 impose de TOLÉRER l'ajout de colonnes, jamais de le refuser : notre
    // export est une version antérieure du format officiel, et il porte déjà une
    // 41ᵉ colonne SANS NOM. Les colonnes non réclamées sont donc listées, pas rejetées.
    const nonReclamees = [];
    (entetes || []).forEach((h, i) => {
        if (utilisees.has(i)) return;
        const brut = String(h ?? '');
        if (normaliserIntitule(brut) === '') return;   // la 41ᵉ colonne sans nom
        nonReclamees.push(brut);
    });

    return {
        colonnes,
        cleManquante: iCle === null,
        ciblesInconnues,
        nonReclamees,
        // Aucun mapping utilisable : l'import n'écrirait rien. Ce n'est pas une erreur
        // de fichier mais un réglage manquant, et le message doit envoyer au bon
        // endroit — l'onglet Paramétrage, pas le fichier.
        aucuneCible: colonnes.filter((c) => c.role === 'donnee').length === 0
    };
}

// ═══════════════════════════════════════════════════════════════════════════════
// 3. CONTRÔLE DE FORME — temps 2 du §0.8
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Pour chaque colonne appariée : combien de valeurs non vides, combien conformes, le
 * taux, et trois exemples. Sous le seuil, la colonne est `refusee` — elle ne sera pas
 * écrite, et la prévisualisation dit laquelle et pourquoi.
 *
 * Le PLANCHER de trois valeurs non conformes joue ici plus qu'ailleurs : le fichier
 * ne porte que 61 lignes, dont 48 avec un classement. Sans plancher, deux cellules
 * fautives feraient 4 % — mais sur un extrait de dix lignes, une seule ferait 10 % et
 * refuserait toute la colonne.
 */
export function controlerColonnesScodoc(colonnes, lignes) {
    return colonnes.map((col) => {
        if (!col.trouvee) {
            return { ...col, nbNonVides: 0, nbConformes: 0, taux: null, exemples: [], refusee: false };
        }
        const regle = col.role === 'cle' ? CHAMP_CLE : CIBLES_SCODOC[col.cible];

        let nbNonVides = 0;
        let nbConformes = 0;
        const exemples = [];
        const contreExemples = [];

        for (const ligne of lignes) {
            const brut = ligne.cellules[col.index];
            if (estVide(brut)) continue;
            nbNonVides++;
            if (regle.valider(brut)) {
                nbConformes++;
                if (exemples.length < 3) {
                    const source = texte(brut);
                    const ecrit = String(regle.convertir(brut) ?? '');
                    exemples.push({ source, ecrit, converti: ecrit !== source });
                }
            } else if (contreExemples.length < 3) {
                contreExemples.push({ ligne: ligne.numero, valeur: texte(brut) });
            }
        }

        const taux = nbNonVides === 0 ? null : nbConformes / nbNonVides;
        const nbNonConformes = nbNonVides - nbConformes;

        // Une colonne VIDE n'est pas une colonne fausse : seize des quarante et une
        // colonnes de l'export réel sont vides de bout en bout. Le taux se mesure sur
        // les valeurs non vides, jamais sur le total.
        const refusee = taux !== null
            && taux < SEUIL_CONFORMITE
            && nbNonConformes >= PLANCHER_NON_CONFORMES;

        return { ...col, nbNonVides, nbConformes, nbNonConformes, taux, exemples, contreExemples, refusee };
    });
}

// ═══════════════════════════════════════════════════════════════════════════════
// 4. PRÉPARATION DES LIGNES À TRANSMETTRE
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Ne transmet que la clé et les valeurs conformes des colonnes retenues.
 *
 * Trois refus, tous nommés avec le numéro de ligne DU FICHIER :
 *   · la clé manque ou n'est pas un numéro étudiant → la ligne n'a pas d'identité ;
 *   · le numéro figure déjà plus haut → le serveur ne le verrait pas (il ferait deux
 *     `UPDATE` sur la même inscription, et la dernière ligne gagnerait en silence).
 *     Même règle qu'APOGEE : la seconde occurrence est refusée en nommant la première ;
 *   · une valeur présente mais hors forme → la ligne est refusée, et pas amputée. Ici
 *     la valeur EST la raison d'être de la ligne : la laisser tomber transmettrait une
 *     ligne vide, comptée « ignorée », et l'utilisateur croirait que le fichier ne
 *     portait rien pour cet étudiant.
 */
export function preparerLignesScodoc(colonnes, lignes) {
    const cle = colonnes.find((c) => c.role === 'cle');
    const retenues = colonnes.filter((c) => c.role === 'donnee' && c.trouvee && !c.refusee);
    const preparees = [];
    const refus = [];
    const vues = new Map();

    for (const ligne of lignes) {
        const brutCle = cle && cle.index !== null ? ligne.cellules[cle.index] : null;
        if (!CHAMP_CLE.valider(brutCle)) {
            refus.push({
                ligne: ligne.numero, motif: 'cle_absente',
                message: estVide(brutCle)
                    ? 'Numéro étudiant absent'
                    : `Numéro étudiant « ${texte(brutCle)} » : ce n'est pas ${CHAMP_CLE.forme}`
            });
            continue;
        }
        const code = CHAMP_CLE.convertir(brutCle);

        if (vues.has(code)) {
            refus.push({
                ligne: ligne.numero, motif: 'doublon_fichier',
                message: `Le numéro ${code} figure déjà ligne ${vues.get(code)}`
            });
            continue;
        }

        const objet = { ligne: ligne.numero, code_etudiant: code };
        let malForme = null;
        for (const col of retenues) {
            const brut = ligne.cellules[col.index];
            if (estVide(brut)) continue;
            const regle = CIBLES_SCODOC[col.cible];
            if (!regle.valider(brut)) {
                malForme = { col, valeur: texte(brut) };
                break;
            }
            objet[col.cible] = regle.convertir(brut);
        }
        if (malForme) {
            refus.push({
                ligne: ligne.numero, motif: 'valeur_hors_bornes',
                message: `${malForme.col.intituleSource} : « ${malForme.valeur} » n'est pas `
                       + `${malForme.col.forme} (n° ${code})`
            });
            continue;
        }

        vues.set(code, ligne.numero);
        preparees.push(objet);
    }

    return { lignes: preparees, refus };
}

// ═══════════════════════════════════════════════════════════════════════════════
// 5. L'ENCHAÎNEMENT COMPLET
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Analyse complète d'une feuille : appariement, contrôle de forme, lignes prêtes.
 * C'est ce que la prévisualisation affiche, et rien ne s'écrit avant.
 */
export function analyserFeuilleScodoc(matrice, options) {
    const { cleLiaison, mapping, champsAutorises } = options || {};
    const { entetes, lignes, nbLignesDeclarees } = decouperFeuille(matrice);
    const appariement = apparierColonnesScodoc(entetes, { cleLiaison, mapping, champsAutorises });
    const colonnes = controlerColonnesScodoc(appariement.colonnes, lignes);
    const { lignes: pretes, refus } = preparerLignesScodoc(colonnes, lignes);

    const colCle = colonnes.find((c) => c.role === 'cle');
    const cleRefusee = colCle ? colCle.refusee : false;

    // Combien de lignes apportent réellement une valeur : c'est le seul chiffre qui
    // dise à quoi sert l'import. 61 lignes lues dont 48 avec un classement, sur le
    // fichier réel — annoncer « 61 » ferait attendre 61 mises à jour.
    const nbAvecValeur = pretes.filter(
        (l) => Object.keys(l).some((k) => k !== 'ligne' && k !== 'code_etudiant')
    ).length;

    return {
        entetes,
        nbLignesDeclarees,
        nbLignesUtiles: lignes.length,
        colonnes,
        colonnesRefusees: colonnes.filter((c) => c.refusee),
        ciblesInconnues: appariement.ciblesInconnues,
        nonReclamees: appariement.nonReclamees,
        cleManquante: appariement.cleManquante,
        cleRefusee,
        aucuneCible: appariement.aucuneCible,
        lignes: pretes,
        nbAvecValeur,
        // Quels champs ce fichier apporte réellement, et si l'un d'eux exige le
        // semestre du relevé. L'écran s'en sert pour n'afficher le sélecteur que
        // quand il sert : le réclamer sur un export de recrutement serait réclamer
        // une information que l'utilisateur n'a pas.
        champsApportes: [...new Set(pretes.flatMap(
            (l) => Object.keys(l).filter((k) => k !== 'ligne' && k !== 'code_etudiant')
        ))],
        exigeSemestre: pretes.some((l) => Object.keys(l).some(
            (k) => CIBLES_SCODOC[k] && CIBLES_SCODOC[k].exigeSemestre
        )),
        refus,
        bloquant: appariement.aucuneCible
            ? 'Aucune colonne SCODOC n\'est associée à un champ de l\'application. '
              + 'À régler dans l\'onglet Paramétrage, carte « Mapping colonnes SCODOC ».'
            : appariement.cleManquante
                ? `La colonne clé « ${cleLiaison} » est absente du fichier`
                : cleRefusee
                    ? `La colonne clé « ${cleLiaison} » ne contient pas des numéros étudiants`
                    : pretes.length === 0
                        ? 'Aucune ligne exploitable dans cette feuille'
                        : null
    };
}

/**
 * Analyse d'un collage. Même moteur que le fichier — seule la façon d'obtenir la
 * matrice change.
 *
 * Le diagnostic propre au collage est le même qu'APOGEE, et pour la même raison :
 * quand la première ligne ne porte aucun intitulé reconnu, c'est presque toujours que
 * l'utilisateur a copié ses lignes SANS la ligne d'en-tête. Annoncer « colonne clé
 * absente » le ferait chercher au mauvais endroit (écart E28).
 */
export function analyserCollageScodoc(texteColle, options) {
    const matrice = matriceDepuisTexte(texteColle);
    if (!matrice.length) {
        return {
            ...analyserFeuilleScodoc([], options),
            bloquant: 'Rien à analyser : la zone de collage est vide'
        };
    }

    const analyse = analyserFeuilleScodoc(matrice, options);
    if (analyse.colonnes.some((c) => c.trouvee)) return analyse;

    return {
        ...analyse,
        bloquant: 'Aucun intitulé de colonne reconnu sur la première ligne collée. '
                + 'Sélectionne aussi la LIGNE DES EN-TÊTES : sans elle, les colonnes ne peuvent '
                + 'être identifiées que par leur position, et ce fichier ne le permet pas.'
    };
}
